<template>
  <div class="banco-horas-wrapper">
    <v-card class="banco-horas-card elevation-4 mx-auto">
      <!-- HEADER -->
      <div class="card-header" :style="{ background: gradienteHeader }">
        <v-btn v-if="view !== 'saldos'" icon dark @click="voltar" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="header-icon" v-if="view === 'saldos'">
          <v-icon size="28" color="white">mdi-clock-check-outline</v-icon>
        </div>
        <div class="header-text">
          <h2 class="header-title">{{ tituloHeader }}</h2>
          <span class="header-sub">Exercício {{ ano }}</span>
        </div>
      </div>

      <v-card-text class="pa-4">
        <!-- VIEW: SALDOS -->
        <template v-if="view === 'saldos'">
          <div v-if="loading" class="estado-central">
            <v-progress-circular indeterminate color="primary" size="48" />
            <span class="estado-texto">Carregando...</span>
          </div>

          <div v-else-if="!saldos.length" class="estado-central">
            <v-icon size="56" color="grey lighten-1">mdi-clock-remove-outline</v-icon>
            <span class="estado-texto grey--text">Nenhum saldo encontrado para este exercício.</span>
          </div>

          <div v-else class="saldos-lista">
            <div
              v-for="item in saldos"
              :key="item.id_matricula"
              class="saldo-item clickable"
              @click="abrirLancamentos(item)"
            >
              <div class="saldo-linha">
                <div class="saldo-info">
                  <span class="saldo-matricula">{{ item.matricula }}</span>
                  <span class="saldo-categoria">{{ item.categoria }}</span>
                </div>
                <div class="saldo-right">
                  <div :class="['saldo-valor', item.saldo_minutos >= 0 ? 'positivo' : 'negativo']">
                    <v-icon small :color="item.saldo_minutos >= 0 ? 'success' : 'error'">
                      {{ item.saldo_minutos >= 0 ? "mdi-arrow-up-circle" : "mdi-arrow-down-circle" }}
                    </v-icon>
                    {{ item.saldo_formatado }}
                  </div>
                  <v-icon small color="grey lighten-1" class="ml-2">mdi-chevron-right</v-icon>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- VIEW: LANCAMENTOS -->
        <template v-if="view === 'lancamentos'">
          <div v-if="loadingLancamentos" class="estado-central">
            <v-progress-circular indeterminate color="primary" size="48" />
            <span class="estado-texto">Carregando lançamentos...</span>
          </div>

          <div v-else-if="!lancamentos.length" class="estado-central">
            <v-icon size="56" color="grey lighten-1">mdi-calendar-remove-outline</v-icon>
            <span class="estado-texto grey--text">Nenhum lançamento encontrado.</span>
          </div>

          <div v-else class="lancamentos-lista">
            <div
              v-for="(item, idx) in lancamentos"
              :key="idx"
              class="lancamento-item clickable"
              @click="abrirDetalhe(item)"
            >
              <div class="lancamento-linha">
                <div class="lancamento-info">
                  <span class="lancamento-data">{{ formatarData(item.data_lancamento) }}</span>
                  <span class="lancamento-tipo">{{ item.tipo_movimento }}</span>
                </div>
                <div class="lancamento-right">
                  <span :class="['lancamento-movimento', item.movimento === '+' ? 'positivo' : 'negativo']">
                    {{ item.movimento }} {{ item.duracao }}
                  </span>
                  <v-icon small color="grey lighten-1" class="ml-2">mdi-chevron-right</v-icon>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- VIEW: DETALHE -->
        <template v-if="view === 'detalhe' && detalhe">
          <div class="detalhe-card">
            <div class="detalhe-linha">
              <span class="detalhe-label">Data</span>
              <span class="detalhe-valor">{{ formatarData(detalhe.data_lancamento) }}</span>
            </div>
            <v-divider class="my-2" />
            <div class="detalhe-linha">
              <span class="detalhe-label">Tipo</span>
              <span class="detalhe-valor">{{ detalhe.tipo_movimento }}</span>
            </div>
            <v-divider class="my-2" />
            <div class="detalhe-linha">
              <span class="detalhe-label">Movimento</span>
              <span :class="['detalhe-valor font-weight-bold', detalhe.movimento === '+' ? 'positivo' : 'negativo']">
                {{ detalhe.movimento }} {{ detalhe.duracao }}
              </span>
            </div>
            <template v-for="(val, key) in detalhesExtras">
              <v-divider :key="'d-' + key" class="my-2" />
              <div :key="key" class="detalhe-linha">
                <span class="detalhe-label">{{ key }}</span>
                <span class="detalhe-valor">{{ val }}</span>
              </div>
            </template>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ServidorServices from "@/services/rh/ServidorServices";

const CAMPOS_PRINCIPAIS = ["data_lancamento", "tipo_movimento", "movimento", "duracao"];

const CAMPOS_OCULTOS = ["fkinstituicao", "anomes", "ordem", "minutos", "mes", "ano", "acrescimo"];

function ocultarCampo(key, _val, detalhe) {
  if (key.toLowerCase().includes("id")) return true;
  if (CAMPOS_OCULTOS.includes(key.toLowerCase())) return true;
  if (key === "adicional" || key === "acrescimo") {
    return !(Number(detalhe.id_categoria) === 3 && detalhe.movimento === "+");
  }
  return false;
}

export default {
  name: "BancoHoras",
  data: () => ({
    loading: false,
    loadingLancamentos: false,
    ano: new Date().getFullYear(),
    view: "saldos",
    matriculaSelecionada: null,
    detalhe: null,
  }),
  computed: {
    ...mapGetters({
      saldos: "storeServidor/getSaldoBancoHoras",
      lancamentos: "storeServidor/getLancamentosBancoHoras",
    }),
    usuarioLogado() {
      return this.$store.getters["config/getUsuarioLogado"];
    },
    tituloHeader() {
      if (this.view === "lancamentos" && this.matriculaSelecionada) {
        return `Matrícula ${this.matriculaSelecionada.matricula}`;
      }
      if (this.view === "detalhe") return "Detalhe do Lançamento";
      return "Banco de Horas";
    },
    gradienteHeader() {
      if (this.view === "lancamentos") return "linear-gradient(135deg, #00695c 0%, #26a69a 100%)";
      if (this.view === "detalhe") return "linear-gradient(135deg, #4527a0 0%, #7e57c2 100%)";
      return "linear-gradient(135deg, #1565c0 0%, #1e88e5 100%)";
    },
    detalhesExtras() {
      if (!this.detalhe) return {};
      return Object.fromEntries(
        Object.entries(this.detalhe)
          .filter(
            ([k, v]) => !CAMPOS_PRINCIPAIS.includes(k) && v !== null && v !== "" && !ocultarCampo(k, v, this.detalhe),
          )
          .map(([k, v]) => [this.formatarChave(k), this.formatarValorExtra(k, v)]),
      );
    },
  },
  methods: {
    async abrirLancamentos(item) {
      this.matriculaSelecionada = item;
      this.view = "lancamentos";
      this.loadingLancamentos = true;
      try {
        await ServidorServices.lancamentosMatriculas(item.id_matricula, this.ano);
      } catch (e) {
        this.$toast.error("Erro ao carregar lançamentos.");
      } finally {
        this.loadingLancamentos = false;
      }
    },
    abrirDetalhe(item) {
      this.detalhe = item;
      this.view = "detalhe";
    },
    voltar() {
      if (this.view === "detalhe") {
        this.view = "lancamentos";
        this.detalhe = null;
      } else if (this.view === "lancamentos") {
        this.view = "saldos";
        this.matriculaSelecionada = null;
        this.$store.commit("storeServidor/setLancamentosBancoHoras", []);
      }
    },
    formatarData(valor) {
      if (!valor) return "-";
      const d = new Date(valor);
      if (isNaN(d)) return valor;
      return d.toLocaleDateString("pt-BR");
    },
    formatarChave(key) {
      return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatarValorExtra(key, val) {
      if (key === "adicional" || key === "acrescimo") {
        return `${Math.round((Number(val) - 1) * 100)}%`;
      }
      return val;
    },
  },
  async mounted() {
    this.loading = true;
    try {
      await ServidorServices.saldoBancoHoras(this.usuarioLogado.pkpessoa, this.ano);
    } catch (e) {
      this.$toast.error("Erro ao carregar saldo de banco de horas.");
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.banco-horas-wrapper {
  padding: 24px 16px;
  display: flex;
  justify-content: center;
}

.banco-horas-card {
  width: 100%;
  max-width: 560px;
  border-radius: 16px !important;
  overflow: hidden;
}

.card-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.header-title {
  color: white;
  font-weight: 400;
  font-size: 1.25rem;
  margin: 0;
  line-height: 1.2;
}

.header-sub {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  margin-top: 2px;
}

.estado-central {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  gap: 12px;
}

.estado-texto {
  font-size: 0.95rem;
  color: #757575;
}

/* SALDOS */
.saldos-lista,
.lancamentos-lista {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.saldo-item,
.lancamento-item {
  border-radius: 10px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  padding: 14px 16px;
  transition: box-shadow 0.2s, background 0.15s;
}

.clickable {
  cursor: pointer;
}

.saldo-item.clickable:hover,
.lancamento-item.clickable:hover {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  background: #eef2ff;
}

.saldo-linha,
.lancamento-linha {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.saldo-right,
.lancamento-right {
  display: flex;
  align-items: center;
}

.saldo-info,
.lancamento-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.saldo-matricula {
  font-size: 0.95rem;
  font-weight: 600;
  color: #212121;
  letter-spacing: 0.3px;
}

.saldo-categoria {
  font-size: 0.78rem;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.saldo-valor {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

/* LANCAMENTOS */
.lancamento-data {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212121;
}

.lancamento-tipo {
  font-size: 0.78rem;
  color: #757575;
}

.lancamento-movimento {
  font-size: 1rem;
  font-weight: 700;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

/* DETALHE */
.detalhe-card {
  padding: 8px 0;
}

.detalhe-linha {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  gap: 12px;
}

.detalhe-label {
  font-size: 0.82rem;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
}

.detalhe-valor {
  font-size: 0.95rem;
  color: #000099;
  text-align: center;
}

/* CORES */
.positivo {
  color: #2e7d32;
}

.negativo {
  color: #c62828;
}

@media (max-width: 600px) {
  .banco-horas-wrapper {
    padding: 12px 8px;
  }
  .card-header {
    padding: 16px;
  }
  .saldo-item,
  .lancamento-item {
    padding: 12px;
  }
}
</style>
